import { datadogRum } from '@datadog/browser-rum';
import { Alert } from '@ksyos/design-system';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import { getJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';

const fetchVersionTimeoutMs = 5 * 60 * 1000;
const maxConsecutiveErrors = 48; // ~4 hours

export function VersionChecker() {
    const translate = getSelectedLanguage().versionChecker;
    const timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null> =
        useRef<ReturnType<typeof setTimeout>>(null);
    const [backendVersion, setBackendVersion] = useState<string>();
    const [showAlert, setShowAlert] = useState(false);
    const previousVersionRef = useRef<string>();
    const consecutiveErrorCount = useRef(0);

    useEffect(() => {
        fetchVersion();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!backendVersion) {
            return;
        }

        if (!previousVersionRef.current) {
            previousVersionRef.current = backendVersion;

            return;
        }

        if (backendVersion !== previousVersionRef.current) {
            previousVersionRef.current = backendVersion;
            datadogRum.addAction('show-new-version-alert');
            setShowAlert(true);
        }
    }, [backendVersion]);

    if (!showAlert) {
        return null;
    }

    return (
        <Alert
            appearance="info"
            hasIcon={true}
            isClosable={true}
            noMargin={true}
            onClose={() => {
                datadogRum.addAction('close-new-version-alert');
                setShowAlert(false);
            }}
        >
            <Alert.Text
                fontWeight="bold"
                mb={2}
                size="md"
            >
                {translate.newVersionAvailableTitle}
            </Alert.Text>

            <Alert.Text
                mb={4}
                size="md"
            >
                {translate.newVersionAvailableText}
            </Alert.Text>

            <Alert.Button
                iconLeft="refresh"
                onClick={() => {
                    datadogRum.addAction('reload-new-version');
                    window.location.reload();
                }}
            >
                {translate.refreshApplication}
            </Alert.Button>
        </Alert>
    );

    async function fetchVersion() {
        try {
            const { json: result } = await getJson('/api/monitor');

            if (result.BUILD_VERSION) {
                setBackendVersion(result.BUILD_VERSION);
            }

            consecutiveErrorCount.current = 0;
        } catch (error) {
            datadogRum.addError(error);
            consecutiveErrorCount.current += 1;
        }

        if (consecutiveErrorCount.current < maxConsecutiveErrors) {
            timeoutRef.current = setTimeout(fetchVersion, fetchVersionTimeoutMs);
        }

        datadogRum.addAction('fetch-new-version-max-errors-reached');
    }
}
